/**
 * ConfirmDialog Atom — controlled confirmation modal built on Modal.
 * @layer atom
 * @package @plerity/ui-core
 */

import { Modal } from "../Modal";
import { cn } from "../utils/cn";
import type { ConfirmDialogProps } from "./ConfirmDialog.types";

function Spinner() {
  return (
    <svg aria-hidden="true" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "default",
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Escape is handled by Modal's built-in useEffect — it calls onClose which we map to onCancel
  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      title={title}
      size="sm"
      showCloseButton={false}
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={cn(
              "rounded-md px-4 py-2 text-sm font-medium transition-colors",
              "border border-gray-300 dark:border-gray-600",
              "bg-white dark:bg-gray-700",
              "text-gray-700 dark:text-gray-200",
              "hover:bg-gray-50 dark:hover:bg-gray-600",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            )}
          >
            {cancelLabel}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
              variant === "danger"
                ? ["bg-red-600 text-white hover:bg-red-700", "focus-visible:ring-red-500"]
                : ["bg-blue-600 text-white hover:bg-blue-700", "focus-visible:ring-blue-500"]
            )}
          >
            {loading && <Spinner />}
            {confirmLabel}
          </button>
        </div>
      }
    >
      {message && <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>}
    </Modal>
  );
}

ConfirmDialog.displayName = "ConfirmDialog";
